import React, { useState } from "react";
import { useAppContext } from "../AppContext";
import Modal from "react-bootstrap/Modal";
import { Form,  Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ExcelJS from 'exceljs';
import moment from 'moment';
import { MODAL_TYPES } from "../modals/ModalManager";

function ImportRiskModal({ show, handleClose, riskTemplates }) {
  const { t } = useTranslation();
  const { currentOrg, openModal } = useAppContext();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [savedSession, setSavedSession] = useState(null);

  // Check for a saved import session every time the modal opens
  React.useEffect(() => {
    if (!show || !currentOrg?.id) return;
    const SESSION_KEY = `riskImportSession_${currentOrg.id}`;
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      setSavedSession(saved ? JSON.parse(saved) : null);
    } catch (e) {
      localStorage.removeItem(SESSION_KEY);
      setSavedSession(null);
    }
  }, [show, currentOrg]);

  const handleResumeSession = () => {
    handleClose();
    openModal(MODAL_TYPES.RISK, {
      riskObj: savedSession.importedRisks,
      resumeFromIndex: savedSession.currentRiskIndex,
      resumePreviousOwners: savedSession.previousOwners,
    });
  };

  const handleDiscardSession = () => {
    const SESSION_KEY = `riskImportSession_${currentOrg.id}`;
    localStorage.removeItem(SESSION_KEY);
    setSavedSession(null);
  };

  // Auto-select template if only one exists
  React.useEffect(() => {
    if (riskTemplates && riskTemplates.length === 1 && !selectedTemplate) {
      setSelectedTemplate(riskTemplates[0]);
    }
  }, [riskTemplates, selectedTemplate]);

   // Utility function to interpret boolean values
  const interpretBoolean = (value) => {
    if(!value) return false;
    const stringValue = String(value).trim().toLowerCase();
    // Returns false if the cell is empty or contains "no", true otherwise
    return stringValue !== "" && stringValue !== "no" && stringValue !== "0" && stringValue !== "false" && stringValue !== "n" && stringValue !== "f" && stringValue !== "nee"; 
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (savedSession) {
      if (!window.confirm(t('start_new_import_confirm'))) {
        e.target.value = '';
        return;
      }
      handleDiscardSession();
    }

    const workbook = new ExcelJS.Workbook();
    const arrayBuffer = await file.arrayBuffer();
    await workbook.xlsx.load(arrayBuffer);
    const worksheet = workbook.worksheets[0]; // Assuming data is in the first worksheet

    const risks = [];
    worksheet.eachRow((row, rowNumber) => {
      // Assuming the first row contains headers and actual data starts from the second row
      if (rowNumber > 1) {
        let [riskName, owner, date, confidentiality, integrity, availability] = row.values.slice(1); // slice(1) to skip the internal index
        // Parse date using moment, fallback to input if parsing fails
        date = moment(date).isValid() ? moment(date).format('YYYY-MM-DD') : '';
        
        // Interpret boolean values according to the specified rules
        confidentiality = interpretBoolean(confidentiality);
        integrity = interpretBoolean(integrity);
        availability = interpretBoolean(availability);

        risks.push({
          description: riskName,
          owner,
          date, 
          confidentiality,
          integrity,
          availability,
        });


      }
    });

    handleClose();

    openModal(MODAL_TYPES.RISK, { riskObj: risks });
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header>
        <Modal.Title>{t('import_risks')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="update-score-modal">
        {savedSession && (
          <div className="alert alert-warning" role="alert" style={{ marginBottom: '16px' }}>
            <strong>{t('paused_import_session')}</strong>
            <p style={{ marginTop: '6px', marginBottom: '10px' }}>
              {t('paused_import_description', {
                current: savedSession.currentRiskIndex + 1,
                total: savedSession.totalRisks,
                date: moment(savedSession.savedAt).format('DD MMM YYYY HH:mm'),
              })}
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div className="white-border-btn submit-btn" style={{fontSize: 16}} onClick={handleResumeSession}>{t('resume_session')}</div>
              <div className="white-border-btn cancel-btn" style={{fontSize: 16}} onClick={handleDiscardSession}>{t('discard_session')}</div>
            </div>
          </div>
        )}
        <Form id="importRiskForm">
          <Row className="mb-3">

              <Form.Group controlId="riskTemplate">
                  <Form.Label>{t('risk_template')} ({currentOrg.securityStandard})</Form.Label>
                  {riskTemplates && riskTemplates.length === 1 ? (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: 'var(--light-blue-bg-color)',
                      borderRadius: '6px',
                      border: '2px solid var(--primary-color)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {riskTemplates[0].name}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--gray-text-color)' }}>
                        {riskTemplates[0].risks.length} {t('risks_plural').toLowerCase()}
                      </div>
                    </div>
                  ) : (
                    <Form.Select
                      value={selectedTemplate ? selectedTemplate['id'] : ''}
                      onChange={e => setSelectedTemplate(riskTemplates.find(template => template['id'] === e.target.value))}
                    >
                      <option value="">{t('select')} {t('template').toLowerCase()}</option>
                      {riskTemplates.map(template => (
                        <option key={template['id']} value={template['id']}>
                          {template.name} ({template.risks.length} {t('risks_plural').toLowerCase()})
                        </option>
                      ))}
                    </Form.Select>
                  )}
              </Form.Group>
          </Row>

          <div className="or-divider-container">
            <div className="or-divider-line" />
            <span className="or-divider-text">{t('or')}</span>
            <div className="or-divider-line" />
          </div>

          <div>{t('excel_import_help_text')}<br/>{t('example')}: <i className="icon-file-excel" /> <a href="/files/example-risk-import.xlsx" style={{textDecoration: 'underline'}}>example-risk-import.xlsx</a></div>
        
          <Row className="mb-3">
            <Form.Group controlId="riskTemplateFile">
              <Form.Label>Upload {t('risks').toLowerCase()} {t('file')}</Form.Label>
              <Form.Control type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
            </Form.Group>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="white-border-btn cancel-btn" onClick={handleClose}>{t('cancel')}</div>
        <div style={{flexGrow: 1}} />
        <div 
          className={`white-border-btn submit-btn ${!selectedTemplate ? 'disabled' : ''}`}
          onClick={() =>{
            if (!selectedTemplate) return;
            if (savedSession) {
              if (!window.confirm(t('start_new_import_confirm'))) return;
              handleDiscardSession();
            }
            handleClose();
            openModal(MODAL_TYPES.RISK, { riskObj: selectedTemplate.risks });
          }}
          style={{
            opacity: !selectedTemplate ? 0.5 : 1,
            cursor: !selectedTemplate ? 'not-allowed' : 'pointer'
          }}
        >{t('import_risks')}</div>

      </Modal.Footer>
    </Modal>
  );
}

export default ImportRiskModal;
